import { useEffect } from 'react'
import {
  ChevronUp,
  Hand,
  Ear,
  EarOff,
  Loader2,
  Mic,
  MicOff,
  MonitorUp,
  PhoneOff,
  Video,
  VideoOff,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  MeetingParticipant,
  TranscriptionPauseReason,
  TranscriptionState,
} from '@hub/contracts'
import { ParticipantsPopover } from './participants-popover'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

/**
 * The bottom-centre control bar (PLAN-085 §1.5). `m` toggles the microphone,
 * because that is the one control people reach for mid-sentence.
 */
export function ControlBar({
  micOn,
  cameraOn,
  sharing,
  audioDevices,
  videoDevices,
  audioDeviceId,
  videoDeviceId,
  onSelectAudioDevice,
  onSelectVideoDevice,
  panelOpen,
  handRaised,
  participants,
  participantList,
  mediaUserIds,
  selfUserId,
  transcription,
  pausedBy,
  canPauseTranscription,
  canEnd,
  busy,
  onToggleMic,
  onToggleTranscription,
  onToggleCamera,
  onToggleShare,
  onTogglePanel,
  onToggleHand,
  onLeave,
  onEnd,
}: {
  micOn: boolean
  cameraOn: boolean
  sharing: boolean
  /** In-call device switching (board m5gc #11): the browser's inputs and the current one. */
  audioDevices: MediaDeviceInfo[]
  videoDevices: MediaDeviceInfo[]
  audioDeviceId: string | null
  videoDeviceId: string | null
  onSelectAudioDevice: (deviceId: string) => void
  onSelectVideoDevice: (deviceId: string) => void
  panelOpen: boolean
  /** Your own hand is up (board m5gc #12). */
  handRaised: boolean
  participants: number
  /** The room's list + who is in the media session, for the popover (board m5gc #16). */
  participantList: MeetingParticipant[]
  mediaUserIds: ReadonlySet<string>
  selfUserId: string | undefined
  transcription: TranscriptionState
  pausedBy: TranscriptionPauseReason | null
  /** The host: may pause and resume the room's transcription. */
  canPauseTranscription: boolean
  canEnd: boolean
  busy?: boolean
  onToggleMic: () => void
  onToggleTranscription: () => void
  onToggleCamera: () => void
  onToggleShare: () => void
  onTogglePanel: () => void
  onToggleHand: () => void
  onLeave: () => void
  onEnd: () => void
}) {
  const { t } = useTranslation('meet')

  // `m` toggles the mic — but never while someone is typing in the document.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'm' && event.key !== 'M') return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target?.isContentEditable) return
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      event.preventDefault()
      onToggleMic()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onToggleMic])

  const hostPaused = transcription === 'paused' && pausedBy === 'host'

  return (
    <TooltipProvider delayDuration={300}>
      <div className="fixed bottom-4 left-1/2 z-40 flex max-w-[calc(100vw-1rem)] -translate-x-1/2 items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900/95 px-1.5 py-1.5 text-white shadow-xl backdrop-blur md:gap-1.5 md:px-2">
        <div className="flex items-center">
          <BarButton
            active={micOn}
            label={micOn ? t('controls.micOn') : t('controls.micOff')}
            hint={t('controls.micShortcut')}
            onClick={onToggleMic}
            testId="toggle-mic"
            className="relative overflow-hidden"
          >
            {/* Level meter (board m5gc #20): fills from the bottom with the
                live input level, driven by the --mic-level CSS variable the
                stage sets per frame — no render per frame. */}
            {micOn && (
              <span
                aria-hidden
                data-testid="mic-level"
                className="pointer-events-none absolute inset-x-0 bottom-0 bg-emerald-400/70 transition-[height] duration-75"
                style={{ height: 'calc(var(--mic-level, 0) * 100%)' }}
              />
            )}
            {micOn ? <Mic className="relative h-4 w-4" /> : <MicOff className="relative h-4 w-4" />}
          </BarButton>
          <DeviceMenu
            label={t('devices.microphone')}
            devices={audioDevices}
            current={audioDeviceId}
            onSelect={onSelectAudioDevice}
            testId="mic-devices"
          />
        </div>

        <div className="flex items-center">
          <BarButton
            active={cameraOn}
            label={cameraOn ? t('controls.cameraOn') : t('controls.cameraOff')}
            onClick={onToggleCamera}
          >
            {cameraOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
          </BarButton>
          <DeviceMenu
            label={t('devices.camera')}
            devices={videoDevices}
            current={videoDeviceId}
            onSelect={onSelectVideoDevice}
            testId="camera-devices"
          />
        </div>

        <BarButton
          active={sharing}
          kind="feature"
          label={sharing ? t('controls.shareOn') : t('controls.shareOff')}
          onClick={onToggleShare}
        >
          <MonitorUp className="h-4 w-4" />
        </BarButton>

        <BarButton
          active={panelOpen}
          kind="feature"
          label={panelOpen ? t('controls.panelOn') : t('controls.panelOff')}
          onClick={onTogglePanel}
        >
          {panelOpen ? (
            <PanelRightClose className="h-4 w-4" />
          ) : (
            <PanelRightOpen className="h-4 w-4" />
          )}
        </BarButton>

        <BarButton
          active={handRaised}
          kind="hand"
          label={handRaised ? t('controls.lowerHand') : t('controls.raiseHand')}
          onClick={onToggleHand}
          testId="toggle-hand"
        >
          <Hand className="h-4 w-4" />
        </BarButton>

        {canPauseTranscription && (
          <BarButton
            active={!hostPaused}
            label={
              hostPaused ? t('controls.resumeTranscription') : t('controls.pauseTranscription')
            }
            onClick={onToggleTranscription}
            testId="toggle-transcription"
          >
            {hostPaused ? <EarOff className="h-4 w-4" /> : <Ear className="h-4 w-4" />}
          </BarButton>
        )}

        <div className="mx-1 h-6 w-px bg-zinc-700" />

        <ParticipantsPopover
          count={participants}
          participants={participantList}
          mediaUserIds={mediaUserIds}
          selfUserId={selfUserId}
        />

        <div className="mx-1 h-6 w-px bg-zinc-700" />

        <Button
          size="sm"
          variant="secondary"
          className="h-8 rounded-full px-3 text-xs"
          onClick={onLeave}
          disabled={busy}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <PhoneOff className="h-3.5 w-3.5" />
          )}
          <span className="ml-1.5 hidden md:inline">{t('controls.leave')}</span>
        </Button>

        {canEnd && (
          <Button
            size="sm"
            variant="destructive"
            className="h-8 rounded-full px-3 text-xs"
            disabled={busy}
            data-testid="end-meeting"
            aria-label={t('controls.end')}
            onClick={onEnd}
          >
            <PhoneOff className="h-3.5 w-3.5 md:hidden" />
            <span className="hidden md:inline">{t('controls.end')}</span>
          </Button>
        )}
      </div>
    </TooltipProvider>
  )
}

const NEUTRAL = 'bg-zinc-700 hover:bg-zinc-600'
const RED = 'bg-red-600/90 hover:bg-red-600'
const BAR_BUTTON_TONE: Record<'device' | 'feature' | 'hand', { on: string; off: string }> = {
  device: { on: NEUTRAL, off: RED },
  feature: { on: RED, off: NEUTRAL },
  hand: { on: 'bg-amber-500 text-black hover:bg-amber-400', off: NEUTRAL },
}

function BarButton({
  active,
  label,
  hint,
  onClick,
  testId,
  kind = 'device',
  className,
  children,
}: {
  active: boolean
  label: string
  hint?: string
  onClick: () => void
  testId?: string
  /**
   * What "active" looks like (board m5gc #19). A device (mic, camera,
   * transcription) is neutral while it works and red when it is off — the
   * Meet convention. A feature (share, panel) is neutral by default and red
   * only while it is on. A hand is amber while raised.
   */
  kind?: 'device' | 'feature' | 'hand'
  className?: string
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={testId}
          onClick={onClick}
          data-active={active ? 'true' : 'false'}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-full transition-colors',
            BAR_BUTTON_TONE[kind][active ? 'on' : 'off'],
            className
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {label}
        {hint ? ` · ${hint}` : ''}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * The small chevron next to the microphone / camera button (board m5gc
 * #11), like Google Meet's: a menu of the browser's inputs with the one in
 * use ticked. Labels are real once permission has been granted; before that
 * the browser hands out empty ones, so the id's head stands in.
 */
function DeviceMenu({
  label,
  devices,
  current,
  onSelect,
  testId,
}: {
  label: string
  devices: MediaDeviceInfo[]
  current: string | null
  onSelect: (deviceId: string) => void
  testId: string
}) {
  const { t } = useTranslation('meet')
  if (devices.length === 0) return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('devices.choose', { kind: label.toLowerCase() })}
          data-testid={testId}
          className="-ml-1 flex h-9 w-5 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-white data-[state=open]:bg-zinc-700 data-[state=open]:text-white"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        className="min-w-56"
        data-testid={`${testId}-menu`}
      >
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={current ?? ''} onValueChange={onSelect}>
          {devices.map((device) => (
            <DropdownMenuRadioItem key={device.deviceId} value={device.deviceId}>
              {device.label || device.deviceId.slice(0, 8)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
